import * as React from 'react';
import { Box, Button, Paper, type SxProps, type Theme, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { HEADER, headerFor } from './headerTokens';

export function EnterprisePage(props: {
  /**
   * The page header strip, drawn above the scrolling content — normally an `EnterpriseToolbar`.
   *
   * ⚠️ It is NOT sticky (this said "sticky toolbar" and nothing here sets `position`), and it is
   * not "breadcrumb + actions": the toolbar carries a breadcrumb, a title, a sub line and actions.
   *
   * 🔴 PASS IT ON EVERY BRANCH OF A PAGE THAT RENDERS `EnterprisePage` MORE THAN ONCE. The four
   * registry screens each render one per state (loading / error / empty / populated), and a
   * toolbar given to some of them makes the page heading appear and disappear as a read resolves.
   * Build it once above the branches and hand the same node to all of them.
   */
  toolbar?: React.ReactNode;
  /** Main page content */
  children: React.ReactNode;
  /** Override main padding; defaults match demo-enterprise-theme */
  padding?: SxProps<Theme>;
  /**
   * WHICH OF THE TWO LAYOUT MODES THIS PAGE IS. **REQUIRED — there is no default.**
   *
   * · `'document'` — the content box is content-sized. It grows, and the scroll container above
   *   scrolls it. Registry list / detail / dashboard / register screens are all this.
   * · `'app-shell'` — the content box is given the scroll container's own height, so a child's
   *   `height: 100%` resolves and the page's panes scroll INTERNALLY instead of growing the page.
   *   `WorkspacePage` and the Reggie takeover under Test Ground are this: fixed-height multi-pane
   *   surfaces whose panes each own a scrollbar, and they need the height chain to reach them.
   *
   * 🔴 IT IS REQUIRED, AND THAT IS THE WHOLE POINT — IT USED TO BE `fill?: boolean`, AND A WHOLE
   * CONSUMER WALKED AROUND THE CONTRACT BY SAYING NOTHING (2026-09-17). A silently-defaulting
   * boolean makes "I chose document mode" and "I never heard of this prop" the same source text,
   * so nothing — not a reviewer, not the compiler, not the guard next door — could tell them
   * apart. `40bcf3632` created `fill`, fixed `WorkspacePage` with it, and did not sweep
   * `ModelHomePage`; Test Ground's Reggie takeover therefore sat in document mode for three weeks
   * with every stage collapsed at the top of the page and the chat transcript growing the page
   * instead of scrolling. A required discriminated prop makes that omission a TypeScript error at
   * the call site rather than a layout defect on DEV. ⛔ Do not give it a default value, and do not
   * widen it back to a boolean.
   *
   * ⚠️ REGRESSION THIS CLOSES (2026-08-27). The R-J #1 width cap below was added in `64784ec95`
   * as `<Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>` — a `height: auto` BLOCK
   * interposed between the scroll container and `props.children`. Before it, children were direct
   * children of the scroll box, whose height is definite after flex layout, so `WorkspacePage`'s
   * grid `height: {lg:'100%'}` resolved and the three panes were bounded. A percentage height
   * against an `auto`-height parent computes to `auto`, so that ONE inserted box silently
   * content-sized the whole workspace: measured on DEV `--0000210`, the pane grid rendered
   * **3231px tall in a 794px viewport**, the chat transcript never got a scrollbar, and the
   * outer page scrolled all three panes as one unit. The commit reasoned about horizontal
   * placement only ("so the scrollbar stays at the viewport edge") — the vertical consequence
   * was not considered. `height: 100%` here restores the pre-`64784ec95` chain WITHOUT moving
   * the cap, which `EnterprisePage.contentWidth.test.tsx` requires to stay on this node.
   */
  layout: 'app-shell' | 'document';
  /**
   * OPT-IN SIDE COLUMN for a `'document'` page, on ultra-wide screens only. `children` stay in the
   * 1400px capped box (R-J #1) and are never widened; `aside` is a 460px column beside it, and
   * `afterMain` (optional) continues the main column under the page's first block. The page uses
   * a container query on its own scroll box, so the rail's width is already accounted for:
   * from 1500px of inner width the three sit in a two-column grid, centred; below that they stack
   * as `children`, `aside`, `afterMain`, the same order as the DOM, each capped at 1400px.
   */
  aside?: { content: React.ReactNode; afterMain?: React.ReactNode };
  /**
   * `'full'` lifts the R-J #1 1400px cap for a page whose frame owns the full width. Operator,
   * 2026-10-07: the auditing workspace's chat panel sits flush against the right edge of the screen.
   * Every other page keeps the default cap.
   */
  width?: 'capped' | 'full';
}) {
  const { aside } = props;
  const WIDE = '@container page (min-width:1500px)';
  const capSx = props.width === 'full' ? { width: '100%' } : { maxWidth: 1400, mx: 'auto', width: '100%' };
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {props.toolbar ? props.toolbar : null}
      <Box
        sx={[
          {
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: { xs: 1.5, md: 3 },
            py: 2.5,
            ...(aside ? { containerType: 'inline-size', containerName: 'page' } : null),
          },
          ...(Array.isArray(props.padding) ? props.padding : [props.padding]),
        ]}
      >
        {/*
         * Content width is ruled 1400px by operator ruling R-J #1 (2026-08-23).
         * The style guide (§10.2) and the deployed PROD original both say 1400; the locked
         * mock's `.layout` says 1560. The operator ruled 1400 so that migrated and
         * un-migrated screens render at the same width — the inconsistency that opened the
         * R-H visual-alignment arm. The cap lives HERE, on the shared wrapper, rather than
         * on each screen: per-screen caps are what produced the four-value divergence
         * (none / 1080 / 1180 / 1400) this ruling exists to remove.
         * The cap is on an inner box, not the scroll container, so the scrollbar stays at
         * the viewport edge while the content centres.
         *
         * ⚠️ `height` IS LOAD-BEARING, NOT COSMETIC — see the `layout` prop above. This node sits
         * in the middle of the percentage-height chain, so leaving it `auto` severs it for every
         * app-shell consumer. Under `'app-shell'` it takes the scroll container's height; under
         * `'document'` it stays `auto` so ordinary pages keep growing and scrolling as before.
         */}
        {aside ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              [WIDE]: {
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1400px) 460px',
                gridTemplateRows: 'auto 1fr',
                columnGap: '24px',
                justifyContent: 'center',
                alignItems: 'start',
              },
            }}
          >
            <Box sx={{ ...capSx, [WIDE]: { gridColumn: 1, gridRow: 1 } }}>{props.children}</Box>
            <Box sx={{ ...capSx, minWidth: 0, [WIDE]: { maxWidth: 'none', mx: 0, gridColumn: 2, gridRow: '1 / span 2' } }}>
              {aside.content}
            </Box>
            {aside.afterMain ? (
              <Box sx={{ ...capSx, [WIDE]: { gridColumn: 1, gridRow: 2 } }}>{aside.afterMain}</Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ ...capSx, ...(props.layout === 'app-shell' ? { height: '100%' } : null) }}>{props.children}</Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * THE PAGE HEADER STRIP (handoff section 8): the only header outside a project.
 *
 * Three text slots, named for what they hold so a caller cannot mount the wrong one in the wrong place:
 *
 * <pre>
 *   breadcrumb  12px / 500 / #6b6b6b   ABOVE the title, not rendered when absent
 *   title       20px / 600 / -.2px     the page's only `h1`
 *   sub         13px / 400 / #404040   BESIDE the title, after a 1px x 20px divider
 * </pre>
 *
 * `breadcrumb` is optional and its element is absent without it, so a screen with no trail holds no
 * empty line above its title. `actions` is likewise absent without content.
 *
 * The title does not wrap (`white-space: nowrap`), as the handoff specifies; the row itself wraps, so
 * the action cluster drops below a title that fills the line.
 */
export function EnterpriseToolbar(props: {
  /** The trail line over the title. Omit it on screens that have none; do not pass `''`. */
  breadcrumb?: React.ReactNode;
  /** The page title, and the page's only `h1`. */
  title: React.ReactNode;
  /** The descriptor beside the title. */
  sub?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const H = headerFor(useTheme());
  return (
    <Paper
      square
      elevation={0}
      sx={{
        flex: 'none',
        borderRadius: 0,
        minHeight: HEADER.minHeight,
        boxSizing: 'border-box',
        px: { xs: '12px', md: '24px' },
        py: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        position: 'relative',
        zIndex: 2,
        background: H.surface,
        borderBottom: H.border,
        boxShadow: H.shadow,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {props.breadcrumb ? <Box data-testid="toolbar-crumb" sx={{ mb: '2px' }}>{props.breadcrumb}</Box> : null}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '14px' }}>
          <Typography
            component="h1"
            // The handoff keeps the title on one line; on a phone a long one ran under the action
            // buttons beside it, so below `md` it wraps within its own column instead.
            sx={{ ...HEADER.title, whiteSpace: { xs: 'normal', md: 'nowrap' }, overflowWrap: 'anywhere', color: H.ink, m: 0 }}
          >
            {props.title}
          </Typography>
          {props.sub ? (
            <>
              <Box aria-hidden sx={{ width: '1px', height: 20, bgcolor: H.divider, flex: 'none' }} />
              <Typography component="div" sx={{ ...HEADER.subtitle, color: H.body }}>
                {props.sub}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>
      {props.actions ? <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>{props.actions}</Box> : null}
    </Paper>
  );
}

const ACTION = HEADER.action;
const actionBase = {
  height: ACTION.height,
  minWidth: 0,
  p: ACTION.padding,
  borderRadius: ACTION.radius,
  fontSize: ACTION.fontSize,
  fontWeight: ACTION.fontWeight,
  lineHeight: 'normal',
  textTransform: 'none',
  whiteSpace: 'nowrap',
} as const;

/** The primary action: 30px, blue gradient, white 13px/600. */
export function EnterprisePrimaryAction(props: React.ComponentProps<typeof Button>) {
  const { sx, ...rest } = props;
  return (
    <Button
      variant="contained"
      color="primary"
      disableElevation
      {...rest}
      sx={[
        {
          ...actionBase,
          background: ACTION.primaryBackground,
          color: '#fff',
          boxShadow: ACTION.primaryShadow,
          // MUI's contained button repaints `primary.main` on hover under `(hover: none)`, which is every
          // tap on a phone; in dark that is #3399ff under white text (2.9:1). Pin the same hover fill.
          '&:hover': {
            background: HEADER.linkDark,
            boxShadow: ACTION.primaryShadow,
            '@media (hover: none)': { background: HEADER.linkDark },
          },
          '&.Mui-disabled': { background: 'action.disabledBackground', color: 'action.disabled', boxShadow: 'none' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

/** The outline action: 30px, 1px blue border, blue 13px/600 on transparent. Two sit in a row, gap 8. */
export function EnterpriseSecondaryAction(props: React.ComponentProps<typeof Button>) {
  const { sx, ...rest } = props;
  const link = headerFor(useTheme()).link;
  return (
    <Button
      variant="outlined"
      color="primary"
      {...rest}
      sx={[
        {
          ...actionBase,
          border: `1px solid ${link}`,
          color: link,
          background: 'transparent',
          '&:hover': { border: `1px solid ${link}`, background: 'transparent' },
          '&.Mui-disabled': { borderColor: 'action.disabledBackground', color: 'action.disabled' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
